import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { api, formatApiError, ROLE_LABELS } from "../lib/apiClient";
import { useAuth } from "../lib/auth";
import BarcodeCameraScanner from "../components/BarcodeCameraScanner";
import { toast } from "sonner";
import { LogOut, RotateCw, Scan, LayoutGrid, CheckCircle2, XCircle, Factory, PackageCheck, Volume2, VolumeX } from "lucide-react";
import { useNavigate } from "react-router-dom";

// --- Sound + Feedback helpers ------------------------------------------------
// Web Audio API — hiçbir dış asset gerekmez. iOS/Chrome autoplay policy için
// AudioContext ilk kullanıcı etkileşiminden sonra resume edilir.
let _audioCtx = null;
function getCtx() {
  if (typeof window === "undefined") return null;
  if (!_audioCtx) {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) _audioCtx = new Ctx();
    } catch (_) { return null; }
  }
  if (_audioCtx?.state === "suspended") _audioCtx.resume().catch(() => {});
  return _audioCtx;
}
function beep({ freq = 880, duration = 140, type = "sine", gain = 0.35, delay = 0 } = {}) {
  const ctx = getCtx();
  if (!ctx) return;
  const t0 = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration / 1000);
  osc.connect(g).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + duration / 1000 + 0.02);
}
function playSuccess() {
  // Tek net, kısa yüksek bip
  beep({ freq: 1200, duration: 90, type: "square", gain: 0.35 });
  beep({ freq: 1600, duration: 90, type: "square", gain: 0.28, delay: 0.08 });
}
function playError() {
  // Alçak iki uzun bip
  beep({ freq: 220, duration: 180, type: "sawtooth", gain: 0.4 });
  beep({ freq: 180, duration: 220, type: "sawtooth", gain: 0.4, delay: 0.22 });
}
// ---------------------------------------------------------------------------

// Cell styling matches the main Order Panel (Sipariş Paneli) — 5-durum renk haritası
const CELL_CLASS = {
  SIPARIS_ALINDI:  "bg-slate-100 text-slate-600 border-slate-400",
  URETIM_BEKLIYOR: "bg-blue-100 text-blue-900 border-blue-500",
  URETIMDE:        "bg-yellow-100 text-yellow-900 border-yellow-500",
  HAZIR:           "bg-emerald-100 text-emerald-900 border-emerald-600",
  SEVK_EDILDI:     "bg-blue-900 text-white border-blue-950",
  EMPTY:           "bg-slate-50 text-slate-300 border-slate-200",
};
const STATUS_LABEL = {
  SIPARIS_ALINDI: "Bekliyor",
  URETIM_BEKLIYOR: "Üretime Hazır",
  URETIMDE: "Üretimde",
  HAZIR: "Hazır",
  SEVK_EDILDI: "Sevk Edildi",
  EMPTY: "—",
};

export default function OperatorHomePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const cfg = user?.role_config;
  const cats = cfg?.categories || [];
  const scanChoice = !!cfg?.scan_choice;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null); // {ok:bool, msg, piece, target}
  const [choiceMode, setChoiceMode] = useState("URETIMDE"); // for baslik_doseme
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem("op_sound") !== "0"; } catch (_) { return true; }
  });
  const [flash, setFlash] = useState(null); // "ok" | "err" | null
  const manualInputRef = useRef(null);
  const lastScanRef = useRef({ code: "", at: 0 });
  const flashTimerRef = useRef(null);

  const toggleSound = () => {
    setSoundOn(v => {
      const next = !v;
      try { localStorage.setItem("op_sound", next ? "1" : "0"); } catch (_) {}
      // Kullanıcı etkileşimi anında AudioContext'i resume et
      if (next) { try { getCtx(); beep({ freq: 900, duration: 80, gain: 0.2 }); } catch (_) {} }
      return next;
    });
  };

  const triggerFeedback = useCallback((ok) => {
    if (soundOn) { ok ? playSuccess() : playError(); }
    setFlash(ok ? "ok" : "err");
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlash(null), ok ? 550 : 800);
  }, [soundOn]);

  useEffect(() => () => { if (flashTimerRef.current) clearTimeout(flashTimerRef.current); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Operatörler arşivi (tam sevk edilmiş siparişleri) görmez
      const { data } = await api.get("/orders/status-matrix", { params: { include_archived: false } });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  // Mode değişince aynı barkodu tekrar tarayabilmek için debounce ref'ini sıfırla
  useEffect(() => { lastScanRef.current = { code: "", at: 0 }; }, [choiceMode]);

  const handleScan = useCallback(async (code) => {
    const raw = (code || "").trim();
    if (!raw) return;
    // Aynı barkod + aynı hedef 2 saniye içinde tekrar tarama = debounce.
    // Farklı hedef (mod değişikliği) için ise izin ver.
    const now = Date.now();
    const effectiveTarget = scanChoice ? choiceMode : (cfg?.scan_action || "");
    const key = `${raw}|${effectiveTarget}`;
    if (lastScanRef.current.code === key && now - lastScanRef.current.at < 2000) return;
    lastScanRef.current = { code: key, at: now };

    if (busy) return;
    setBusy(true);
    try {
      const body = { barcode: raw };
      if (scanChoice) body.target_status = choiceMode;
      const { data } = await api.post("/pieces/scan-action", body);
      const targetLbl = STATUS_LABEL[data.new_status] || data.new_status;
      setLastResult({
        ok: true,
        msg: `${data.piece.category} · ${data.piece.model || "-"} → ${targetLbl}`,
        piece: data.piece,
        target: data.new_status,
      });
      toast.success(`${data.piece.category} · ${data.piece.model || "-"} → ${targetLbl}`);
      try { navigator.vibrate?.(120); } catch (_) {}
      triggerFeedback(true);
      // Refresh panel (fire-and-forget)
      load();
    } catch (e) {
      const msg = formatApiError(e);
      setLastResult({ ok: false, msg, piece: null, target: null });
      toast.error(msg);
      try { navigator.vibrate?.([80, 60, 80]); } catch (_) {}
      triggerFeedback(false);
    } finally {
      setBusy(false);
      setManual("");
      // Refocus manual input for keyboard/PDA scanners
      setTimeout(() => manualInputRef.current?.focus(), 40);
    }
  }, [busy, scanChoice, choiceMode, load, triggerFeedback, cfg]);

  const submitManual = (e) => {
    e.preventDefault();
    if (manual.trim()) handleScan(manual);
  };

  // Filter rows: keep only orders that contain at least one piece in an allowed category
  const filtered = useMemo(() => {
    if (!rows.length || !cats.length) return rows;
    return rows.filter(r => cats.some(c => r.categories?.[c]?.total > 0));
  }, [rows, cats]);

  const [q, setQ] = useState("");
  const searchable = useMemo(() => {
    if (!q) return filtered;
    const s = q.toLowerCase();
    return filtered.filter(r =>
      r.order_number.toLowerCase().includes(s) ||
      (r.reference || "").toLowerCase().includes(s) ||
      (r.customer_name || "").toLowerCase().includes(s) ||
      cats.some(c => (r.categories?.[c]?.model || "").toLowerCase().includes(s))
    );
  }, [filtered, q, cats]);

  const targetInfo = useMemo(() => {
    if (scanChoice) return STATUS_LABEL[choiceMode];
    return STATUS_LABEL[cfg?.scan_action] || "—";
  }, [scanChoice, choiceMode, cfg]);

  return (
    <div className="min-h-screen bg-slate-100 relative">
      {/* Full-screen edge flash — hattaki gürültüde net görsel geri bildirim */}
      {flash && (
        <div
          data-testid={`op-flash-${flash}`}
          aria-hidden="true"
          className={`fixed inset-0 z-[80] pointer-events-none animate-op-flash ${flash === "ok" ? "op-flash-ok" : "op-flash-err"}`}
        />
      )}

      {/* Top bar: role + logout */}
      <header className="sticky top-0 z-30 bg-slate-900 text-white h-14 flex items-center px-3 gap-3 border-b border-slate-800 shadow-md">
        <div className="w-8 h-8 bg-blue-600 grid place-items-center font-display font-black text-white text-lg">M</div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-base truncate" data-testid="op-title">
            {ROLE_LABELS[user?.role] || "Operatör"}
          </div>
          <div className="text-[10px] tracking-widest text-slate-300 uppercase truncate">
            {user?.name} · Hedef: <span className="text-amber-400 font-bold">{targetInfo}</span>
          </div>
        </div>
        <button
          onClick={toggleSound}
          data-testid="op-sound-toggle"
          className={`p-2 rounded ${soundOn ? "text-emerald-300 hover:bg-slate-800" : "text-slate-500 hover:bg-slate-800"}`}
          aria-label={soundOn ? "Sesi kapat" : "Sesi aç"}
          title={soundOn ? "Ses açık" : "Ses kapalı"}
        >
          {soundOn ? <Volume2 size={18}/> : <VolumeX size={18}/>}
        </button>
        <button
          onClick={async () => { await logout(); navigate("/giris"); }}
          data-testid="op-logout-btn"
          className="p-2 hover:bg-slate-800 rounded"
          aria-label="Çıkış"
        >
          <LogOut size={18}/>
        </button>
      </header>

      <div className="max-w-[1200px] mx-auto p-3 sm:p-5 space-y-4">
        {/* Scan card */}
        <section className="card-industrial p-3 sm:p-5" data-testid="op-scan-card">
          <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest font-bold text-slate-600">
            <Scan size={14}/> Barkodu Okutun veya Elle Girin
          </div>

          {scanChoice && (
            <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="op-choice-toggle">
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-600 mr-2">Mod:</div>
              <button
                type="button"
                onClick={() => setChoiceMode("URETIMDE")}
                data-testid="op-mode-uretim"
                className={`inline-flex items-center gap-1 px-3 py-1.5 border-2 font-bold text-xs uppercase ${choiceMode === "URETIMDE" ? "border-amber-600 bg-amber-100 text-amber-900" : "border-slate-300 bg-white text-slate-500"}`}
              >
                <Factory size={13}/> Üretime Al
              </button>
              <button
                type="button"
                onClick={() => setChoiceMode("HAZIR")}
                data-testid="op-mode-hazir"
                className={`inline-flex items-center gap-1 px-3 py-1.5 border-2 font-bold text-xs uppercase ${choiceMode === "HAZIR" ? "border-emerald-600 bg-emerald-100 text-emerald-900" : "border-slate-300 bg-white text-slate-500"}`}
              >
                <PackageCheck size={13}/> Hazır
              </button>
            </div>
          )}

          <form onSubmit={submitManual} className="flex gap-2 mb-3">
            <input
              ref={manualInputRef}
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder="Barkod..."
              autoFocus
              data-testid="op-manual-input"
              className="flex-1 px-3 py-3 border-2 border-slate-800 bg-white font-mono text-base focus:border-blue-700 focus:outline-none"
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="submit"
              disabled={busy || !manual.trim()}
              data-testid="op-submit-btn"
              className="px-5 py-3 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide text-sm disabled:opacity-50"
            >
              {busy ? "..." : "ARA"}
            </button>
          </form>

          <BarcodeCameraScanner
            onDetected={handleScan}
            autoStart={false}
          />

          {lastResult && (
            <div
              data-testid="op-last-result"
              className={`mt-3 p-3 border-2 flex items-start gap-2 ${lastResult.ok ? "bg-emerald-50 border-emerald-600 text-emerald-900" : "bg-red-50 border-red-500 text-red-800"}`}
            >
              {lastResult.ok ? <CheckCircle2 size={20} className="shrink-0 mt-0.5"/> : <XCircle size={20} className="shrink-0 mt-0.5"/>}
              <div className="flex-1 text-sm font-bold">
                {lastResult.ok ? "Başarılı: " : "Hata: "}{lastResult.msg}
              </div>
            </div>
          )}
        </section>

        {/* Order Panel */}
        <section className="card-industrial" data-testid="op-panel">
          <div className="p-3 sm:p-4 flex flex-wrap items-center gap-3 border-b border-slate-200">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-slate-600">
              <LayoutGrid size={14}/> Sipariş Paneli
              <span className="text-slate-400 normal-case font-medium">
                ({cats.join(" · ")})
              </span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ara..."
                data-testid="op-search"
                className="px-3 py-1.5 border border-slate-300 text-sm focus:border-blue-700 focus:outline-none"
              />
              <button onClick={load} data-testid="op-refresh"
                className="p-2 border border-slate-300 hover:bg-slate-900 hover:text-white">
                <RotateCw size={16}/>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 border-b-2 border-slate-300 text-[11px] uppercase tracking-widest text-slate-600">
                  <th className="text-left px-2 py-2 font-bold">Sipariş</th>
                  <th className="text-left px-2 py-2 font-bold">Müşteri</th>
                  <th className="text-left px-2 py-2 font-bold hidden sm:table-cell">Referans</th>
                  <th className="text-left px-2 py-2 font-bold hidden md:table-cell">Ölçü</th>
                  {cats.map(c => (
                    <th key={c} className="text-left px-1 py-2 font-bold">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={4 + cats.length} className="p-6 text-center text-slate-500">Yükleniyor...</td></tr>
                )}
                {!loading && searchable.length === 0 && (
                  <tr><td colSpan={4 + cats.length} className="p-8 text-center text-slate-500">
                    <LayoutGrid size={32} className="mx-auto mb-2 opacity-40"/>
                    Görüntülenecek sipariş yok.
                  </td></tr>
                )}
                {searchable.map((r, i) => (
                  <tr key={r.id} data-testid={`op-row-${r.order_number}`}
                    className={`border-b border-slate-200 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                    <td className="px-2 py-1.5">
                      <div className="font-mono font-bold text-blue-700 text-xs">{r.order_number}</div>
                    </td>
                    <td className="px-2 py-1.5 font-semibold text-xs">{r.customer_name}</td>
                    <td className="px-2 py-1.5 font-mono text-slate-600 text-[10px] hidden sm:table-cell">{r.reference || "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-xs hidden md:table-cell">{r.size || "—"}</td>
                    {cats.map(c => {
                      const cat = r.categories?.[c];
                      return <CategoryCell key={c} cat={cat} order={r.order_number} category={c}/>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-3 border-t border-slate-200 flex flex-wrap gap-1.5 text-[10px]">
            {["SIPARIS_ALINDI", "URETIM_BEKLIYOR", "URETIMDE", "HAZIR", "SEVK_EDILDI"].map(k => (
              <span key={k} className={`px-2 py-1 border-2 font-bold uppercase ${CELL_CLASS[k]}`}>{STATUS_LABEL[k]}</span>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function CategoryCell({ cat, order, category }) {
  if (!cat || !cat.total) {
    return <td className={`px-1 py-1 border-l border-slate-100 text-center text-[10px] ${CELL_CLASS.EMPTY}`}>—</td>;
  }
  // Bottleneck view: en az ilerlemiş parça belirler
  const counts = cat.status_counts || {};
  const total = cat.total;
  const waiting = counts.SIPARIS_ALINDI || 0;
  const readyForProd = counts.URETIM_BEKLIYOR || 0;
  const inProd = counts.URETIMDE || 0;
  const ready = (counts.HAZIR || 0) + (counts.SEVKIYATA_HAZIR || 0);
  const shipped = counts.SEVK_EDILDI || 0;

  let key;
  if (shipped === total) key = "SEVK_EDILDI";
  else if (waiting > 0) key = "SIPARIS_ALINDI";
  else if (readyForProd > 0) key = "URETIM_BEKLIYOR";
  else if (inProd > 0) key = "URETIMDE";
  else if (ready > 0) key = "HAZIR";
  else key = "SIPARIS_ALINDI";

  const doneCount = ready + shipped;

  return (
    <td className="px-0.5 py-1 border-l border-slate-100">
      <div
        data-testid={`op-cell-${order}-${category}`}
        className={`px-1.5 py-1 border-2 ${CELL_CLASS[key]} text-[11px] font-bold leading-tight`}
        title={`${cat.model} · ${doneCount}/${total} hazır`}
      >
        <div className="font-display font-black text-[11px] truncate">{cat.model || "•"}</div>
        <div className="flex items-center justify-between text-[9px] uppercase tracking-wide opacity-90 mt-0.5">
          <span>{STATUS_LABEL[key]}</span>
          <span className="font-mono tabular-nums">{doneCount}/{total}</span>
        </div>
      </div>
    </td>
  );
}
